import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { ICampaignCreatorForm, ICtaForm } from '@campaign-form/campaign-form.interface';
import { TDeliverableForm } from '../../pages/campaigns/campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';

@Injectable({
  providedIn: 'root',
})
export class CreatorCustomValueCheckService {
  private readonly creatorHasCustomValueMap = new Map<string, boolean>();

  public calculateCreatorsCustomValues(
    creatorIds: string[],
    ctaForm?: FormGroup<ICtaForm>['value'],
    campaignCreators?: FormArray<FormGroup<ICampaignCreatorForm>>['value'],
  ): Map<string, boolean> {
    let ctaForms: FormGroup<ICtaForm>['value'][] = [];

    if (campaignCreators) {
      ctaForms = this.getUniqueCtaForms(campaignCreators);
    } else if (ctaForm) {
      ctaForms = [ctaForm];
    }

    for (const creatorId of creatorIds) {
      for (const cf of ctaForms) {
        const key = `${creatorId}_${cf.id!}`;

        this.creatorHasCustomValueMap.set(key, this.checkCreatorCustomValue(creatorId, cf));
      }
    }

    return this.creatorHasCustomValueMap;
  }

  private checkCreatorCustomValue(creatorId: string, ctaForm: FormGroup<ICtaForm>['value']): boolean {
    if (ctaForm.customCtaValues?.some((customCtaValue) => customCtaValue.creatorId === creatorId)) {
      return true;
    }

    for (const deliverable of ctaForm.deliverables ?? []) {
      if (
        deliverable.customCtaValues?.some((customCtaValue) => customCtaValue.creatorId === creatorId) ||
        deliverable.exclusiveTo === creatorId ||
        this.deliverableHasCustomValue(deliverable, creatorId)
      ) {
        return true;
      }
    }

    for (const execution of ctaForm.executions ?? []) {
      if (
        execution.customCtaValues?.some((customCtaValue) => customCtaValue.creatorId === creatorId) ||
        execution.exclusiveTo === creatorId
      ) {
        return true;
      }

      for (const expectedKpi of execution.expectedKpis ?? []) {
        if (
          expectedKpi.customCtaValues?.some((customCtaValue) => customCtaValue.creatorId === creatorId) ||
          expectedKpi.exclusiveTo === creatorId
        ) {
          return true;
        }
      }
    }

    return false;
  }

  private getUniqueCtaForms(
    campaignCreators: FormArray<FormGroup<ICampaignCreatorForm>>['value'],
  ): FormArray<FormGroup<ICampaignCreatorForm>>['value'] {
    const ctaFormMap = new Map<string, FormGroup<ICtaForm>['value']>();

    campaignCreators?.forEach((creator) => {
      const { ctas } = creator;

      ctas?.forEach((cta) => {
        if (!ctaFormMap.has(cta.id!)) {
          ctaFormMap.set(cta.id!, cta);
        }
      });
    });

    return Array.from(ctaFormMap.values());
  }

  private deliverableHasCustomValue(deliverable: FormGroup<TDeliverableForm>['value'], creatorId: string): boolean {
    for (const child of Object.values(deliverable)) {
      if (this.childHasCustomValue(child, creatorId)) {
        return true;
      }
    }

    return false;
  }

  private childHasCustomValue(node: unknown, creatorId: string): boolean {
    if (Array.isArray(node)) {
      const [first] = node as unknown[];

      if (first && typeof first === 'object') {
        const firstObj = first as {
          customCtaValues?: Array<{ creatorId: string }>;
          exclusiveTo?: string | null;
        };

        if ('customCtaValues' in firstObj || 'exclusiveTo' in firstObj) {
          for (const item of node as unknown[]) {
            const obj = item as {
              customCtaValues?: Array<{ creatorId: string }>;
              exclusiveTo?: string | null;
            };

            const hasMatch: boolean = Array.isArray(obj.customCtaValues)
              ? obj.customCtaValues.some((v) => v?.creatorId === creatorId)
              : false;

            if (hasMatch || obj.exclusiveTo === creatorId) {
              return true;
            }
          }
        }
      }

      for (const child of node as unknown[]) {
        if (this.childHasCustomValue(child, creatorId)) {
          return true;
        }
      }

      return false;
    }

    if (node !== null && typeof node === 'object') {
      const obj = node as {
        customCtaValues?: Array<{ creatorId: string }> | null;
        exclusiveTo?: string | null;
      };

      if (Array.isArray(obj.customCtaValues)) {
        const hasMatch: boolean = obj.customCtaValues.some((v) => v?.creatorId === creatorId);

        if (hasMatch) {
          return true;
        }
      }

      if (obj.exclusiveTo === creatorId) {
        return true;
      }

      for (const value of Object.values(node as Record<string, unknown>)) {
        if (this.childHasCustomValue(value, creatorId)) {
          return true;
        }
      }
    }

    return false;
  }
}
